import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { allFields, formConfig } from "../../formConfig";
import { getProgressStages, ProgressStage } from "../../api/admin";

const STORAGE_KEY = "adminSelectedColumns_v2";
const DEFAULT_COLUMNS = ["projectName", "jobNumber", "__submittedBy", "__submittedAt", "__progress"];

function loadColumns(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return DEFAULT_COLUMNS;
}

export default function ColumnsPage() {
  const navigate = useNavigate();
  const [selectedCols, setSelectedCols] = useState<string[]>(loadColumns);
  const [stages, setStages] = useState<ProgressStage[]>([]);

  useEffect(() => { getProgressStages().then(setStages); }, []);
  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedCols)); }, [selectedCols]);

  const fieldsById = useMemo(() => {
    const m = new Map<string, { id: string; question: string; shortLabel?: string; section: string }>();
    formConfig.forEach((sec) => sec.fields.forEach((f) => m.set(f.id, { id: f.id, question: f.question, shortLabel: f.shortLabel, section: sec.shortTitle })));
    return m;
  }, []);

  const builtIns = useMemo(() => {
    const list: { id: string; label: string }[] = [
      { id: "__submittedBy", label: "Submitted by" },
      { id: "__submittedAt", label: "Submitted" },
      { id: "__progress", label: "Progress (all stages)" }
    ];
    stages.forEach((s) => list.push({ id: `__stage:${s.id}`, label: `Stage · ${s.label}` }));
    return list;
  }, [stages]);
  const builtInsById = useMemo(() => new Map(builtIns.map((b) => [b.id, b])), [builtIns]);

  function toggleCol(id: string) {
    setSelectedCols((cols) => (cols.includes(id) ? cols.filter((c) => c !== id) : [...cols, id]));
  }
  function moveCol(id: string, dir: -1 | 1) {
    setSelectedCols((cols) => {
      const i = cols.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= cols.length) return cols;
      const next = cols.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  const [dragId, setDragId] = useState<string | null>(null);
  const [insertIdx, setInsertIdx] = useState<number | null>(null);
  const [overAvailable, setOverAvailable] = useState(false);

  function onDragStart(id: string, e: React.DragEvent) {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  }
  function onDragEnd() {
    setDragId(null);
    setInsertIdx(null);
    setOverAvailable(false);
  }

  function onGapDragOver(i: number, e: React.DragEvent) {
    if (!dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setOverAvailable(false);
    if (insertIdx !== i) setInsertIdx(i);
  }

  function onSelectedDrop(e: React.DragEvent) {
    e.preventDefault();
    const sourceId = dragId || e.dataTransfer.getData("text/plain");
    const dropAt = insertIdx;
    setDragId(null);
    setInsertIdx(null);
    setOverAvailable(false);
    if (!sourceId || dropAt == null) return;
    setSelectedCols((cols) => {
      const next = cols.slice();
      const from = next.indexOf(sourceId);
      let to = dropAt;
      if (from >= 0) {
        next.splice(from, 1);
        if (from < to) to -= 1;
        next.splice(to, 0, sourceId);
      } else {
        next.splice(to, 0, sourceId);
      }
      return next;
    });
  }

  function onAvailableDragOver(e: React.DragEvent) {
    if (!dragId) return;
    if (!selectedCols.includes(dragId)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setInsertIdx(null);
    setOverAvailable(true);
  }
  function onAvailableDrop(e: React.DragEvent) {
    e.preventDefault();
    const sourceId = dragId || e.dataTransfer.getData("text/plain");
    setDragId(null);
    setInsertIdx(null);
    setOverAvailable(false);
    if (!sourceId) return;
    setSelectedCols((cols) => cols.filter((c) => c !== sourceId));
  }

  return (
    <>
      <div className="admin-page-head">
        <h1>Columns</h1>
        <button type="button" className="btn-secondary" onClick={() => navigate("/admin")}>Done</button>
      </div>

      <div className="card">
        <h3 className="card-title">Selected columns (in order)</h3>
        {selectedCols.length === 0 && (
          <div
            className={"empty-drop " + (insertIdx === 0 ? "is-drop-target" : "")}
            onDragOver={(e) => onGapDragOver(0, e)}
            onDrop={onSelectedDrop}
          >Drag columns here</div>
        )}
        <ol className="ordered-list dnd-list">
          <li
            className={"dnd-gap " + (insertIdx === 0 ? "is-drop-target" : "")}
            onDragOver={(e) => onGapDragOver(0, e)}
            onDrop={onSelectedDrop}
            aria-hidden
          />
          {selectedCols.map((id, i) => {
            const f = fieldsById.get(id);
            const b = builtInsById.get(id);
            const stageMatch = id.startsWith("__stage:") ? stages.find((s) => `__stage:${s.id}` === id) : null;
            const label = f ? `${f.section} · ${f.shortLabel || f.question}` : b?.label || stageMatch?.label || id;
            const dragging = dragId === id;
            return (
              <React.Fragment key={id}>
                <li
                  draggable
                  onDragStart={(e) => onDragStart(id, e)}
                  onDragEnd={onDragEnd}
                  className={"dnd-item " + (dragging ? "is-dragging" : "")}
                >
                  <span className="drag-handle" aria-hidden>⋮⋮</span>
                  <span className="ol-actions">
                    <button type="button" onClick={() => moveCol(id, -1)} title="Move up">↑</button>
                    <button type="button" onClick={() => moveCol(id, 1)} title="Move down">↓</button>
                    <button type="button" onClick={() => toggleCol(id)} title="Remove">×</button>
                  </span>
                  <span className="ol-label">{label}</span>
                </li>
                <li
                  className={"dnd-gap " + (insertIdx === i + 1 ? "is-drop-target" : "")}
                  onDragOver={(e) => onGapDragOver(i + 1, e)}
                  onDrop={onSelectedDrop}
                  aria-hidden
                />
              </React.Fragment>
            );
          })}
        </ol>
        <div className="actions">
          <button type="button" className="btn-secondary" onClick={() => setSelectedCols(allFields.map((f) => f.id))}>Select all fields</button>
          <button type="button" className="btn-secondary" onClick={() => setSelectedCols([])}>Clear</button>
          <button type="button" className="btn-secondary" onClick={() => setSelectedCols(DEFAULT_COLUMNS)}>Reset to default</button>
        </div>
      </div>

      <div
        className={"card available-card " + (overAvailable ? "is-drop-target" : "")}
        onDragOver={onAvailableDragOver}
        onDrop={onAvailableDrop}
        onDragLeave={() => setOverAvailable(false)}
      >
        <h3 className="card-title">Available columns <span className="hint">(drag a selected column here to remove · drag from here to add)</span></h3>
        <div className="cat-block">
          <h4>Built-in</h4>
          <div className="col-checks">
            {builtIns.map((b) => {
              const inUse = selectedCols.includes(b.id);
              return (
                <label
                  key={b.id}
                  className={"avail-item " + (inUse ? "in-use" : "")}
                  draggable={!inUse}
                  onDragStart={(e) => { if (!inUse) onDragStart(b.id, e); }}
                  onDragEnd={onDragEnd}
                >
                  <input type="checkbox" checked={inUse} onChange={() => toggleCol(b.id)} />
                  <span className="avail-label">{b.label}</span>
                  {!inUse && <span className="drag-handle small" aria-hidden>⋮⋮</span>}
                </label>
              );
            })}
          </div>
        </div>
        {formConfig.map((sec) => (
          <div key={sec.title} className="cat-block">
            <h4>{sec.title}</h4>
            <div className="col-checks">
              {sec.fields.map((f) => {
                const inUse = selectedCols.includes(f.id);
                return (
                  <label
                    key={f.id}
                    className={"avail-item " + (inUse ? "in-use" : "")}
                    draggable={!inUse}
                    onDragStart={(e) => { if (!inUse) onDragStart(f.id, e); }}
                    onDragEnd={onDragEnd}
                  >
                    <input type="checkbox" checked={inUse} onChange={() => toggleCol(f.id)} />
                    <span className="avail-label">{f.shortLabel || f.question}</span>
                    {!inUse && <span className="drag-handle small" aria-hidden>⋮⋮</span>}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
